import React, {useEffect, useState} from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { ThemeProps, RTLProps } from '@/shared/prop-types/ReducerProps';
import CheckboxField from '@/shared/components/form/CheckboxField';
import { Modal, ModalBody, ModalHeader, Button, ButtonToolbar, Row, Col } from 'reactstrap';
import { Form, Field } from 'react-final-form';
import axios from 'axios'
import {sendFlashMessage} from "@/shared/components/flash";
import formValidation from "@/shared/validation/program-accounting";
import { getProgramAction } from '@/redux/actions/programActions';
import renderSelectField from '@/shared/components/form/Select';
import {isObject} from "../../../../shared/helpers";
import HierarchySaveModal from './HierarchySaveModal';



const prepareForValidation = values => {

    const clean = {
        transaction_fee: values?.transaction_fee ?? null,
        administrative_fee: values?.administrative_fee ?? null,
        balance_threshold: values?.balance_threshold ?? null,
        bill_parent_program: values?.bill_parent_program ?? null,
        administrative_fee_factor: values?.administrative_fee_factor ?? null,
        administrative_fee_calculation: values?.administrative_fee_calculation ?? 'participants',
        calculation: values?.calculation ?? null,
        convenience_fee: values?.convenience_fee ?? null,
        country: values?.country ?? null,
        deposit_fee: values?.deposit_fee ?? null,
        discount_rebate_percentage: values?.discount_rebate_percentage ?? null,
        expiration_rebate_percentage: values?.expiration_rebate_percentage ?? null,
        fee_amount: values?.fee_amount ?? null,
        fixed_fee: values?.fixed_fee ?? null,
        invoice_po_number: values?.invoice_po_number ?? null,
        is_pay_in_advance: values?.is_pay_in_advance ?? null,
        low_balance_email: values?.low_balance_email ?? null,
        monthly_usage_fee: values?.monthly_usage_fee ?? null,
        percent_total_spend_rebate: values?.percent_total_spend_rebate ?? null,
        reserve_percentage: values?.reserve_percentage ?? null,
        cc_email_list: values.cc_email_list ?? null,
        bcc_email_list: values.bcc_email_list ?? null,
        prefix: values.prefix ?? null,
        notification_email_list: values.notification_email_list ?? null,
        root_fixed_fee : values.root_fixed_fee ?? null
    }
    return {...values, ...clean}
}


const AccountingModal = ({dispatch, organization, data, isOpen, setOpen, toggle, theme, rtl, auth}) => {
    const [loading, setLoading] = useState(false)
    const [programTransactionFee, setProgramTransactionFee] = useState([])
    const [programCategories, setProgramCategories] = useState({});
    const [isCascadeConfirmOpen, setCascadeConfirmOpen] = useState(false);
    const [activeCascadeField, setActiveCascadeField] = useState(null);

    // ICOV3-1572: fires immediately on an eligible field's own change, not on
    // Save — see ~/.claude-specs/ICOV3_React/ICOV3-1572-specs.md Step 13.
    const handleCascadeFieldChange = (fieldName, newValue) => {
        setActiveCascadeField({ name: fieldName, value: newValue });
        setCascadeConfirmOpen(true);
    };

    const performSave = async (saveData) => {
        setLoading(true)
        try {
            const response = await axios.put(`/organization/${saveData.organization_id}/program/${saveData.id}`, saveData);
            setLoading(false)
            if (response.status === 200) {
                dispatch(
                    getProgramAction(
                        saveData.organization_id,
                        saveData.id
                    )
                )
                dispatch(sendFlashMessage('Program has been updated', 'alert-success', 'top'))
            }
        } catch(error) {
            setLoading(false)
            let errMessage = '';
            if (error.response) {
                if (error.response.data && error.response.data.errors) {
                    errMessage = Object.keys(error.response.data.errors).map(key =>
                        error.response.data.errors[key]
                    )
                }
            } else if (error.request) {
                errMessage = error.request;
            } else if (error.message)  {
                errMessage = error.message;
            }
            errMessage = JSON.stringify(errMessage);
            dispatch(sendFlashMessage('Program could not be updated.' + ' ' + errMessage, 'alert-danger', 'top'))
        }
    }

    const onSubmitForm = (values) => {
        // Build the merged payload without mutating `data` (the shared Redux
        // program object) so that canceling the save-cascade prompt afterward
        // leaves no stray edits applied — see ICOV3-1572 code review.
        let mergedProgramExtras = data.program_extras;
        if (data.program_extras) {
            const administrativeFeeCalculation = isObject(values.administrative_fee_calculation)
                ? values.administrative_fee_calculation.value
                : values.administrative_fee_calculation;

            mergedProgramExtras = {
                ...data.program_extras,
                administrative_fee_calculation: administrativeFeeCalculation,
                administrative_fee: values.administrative_fee,
                administrative_fee_factor: values.administrative_fee_factor,
            };
        }

        values.is_pay_in_advance = programCategories.is_pay_in_advance;
        values.invoice_for_awards = programCategories.invoice_for_awards;
        const mergedData = {
            ...data,
            ...prepareForValidation(values),
            program_extras: mergedProgramExtras,
            program_transaction_fee: programTransactionFee,
        };

        delete mergedData['merchants'];

        performSave(mergedData);
    }

    useEffect(() => {
        setProgramTransactionFee(data.program_transaction_fee)
    }, [data.program_transaction_fee]);

    useEffect(() => {
        setProgramCategories((prevState)=>({...prevState,
            is_pay_in_advance: data.is_pay_in_advance,
            invoice_for_awards: data.invoice_for_awards,
          }));
    }, [data.is_pay_in_advance, data.invoice_for_awards]);

    if( !organization || !data) return 'loading...'

    const handleChangeTierAmount = (index, value) => {
        const newData = [...programTransactionFee];
        newData[index].tier_amount = value;
        setProgramTransactionFee(newData)
    };


    const handleChangeTransactionFee = (index, value) => {
        const newData = [...programTransactionFee];
        newData[index].transaction_fee = value;
        setProgramTransactionFee(newData)
    };


    const handleRemoveTier = (index) => {
        const newData = [...programTransactionFee];
        newData.splice(index, 1);
        console.log(newData)
        setProgramTransactionFee(newData)
    };

    const handleAddTier = () => {
        const newData = [...programTransactionFee, { tier_amount: '', transaction_fee: '' }];
        setProgramTransactionFee(newData)
    };

    return (
    <>
    <Modal className={`modal-program modal-lg ${theme.className} ${rtl.direction}-support`} isOpen={isOpen} toggle={toggle}>
        <Form
        onSubmit={onSubmitForm}
        validate={(values) => formValidation.validateForm(values)}
        // validate={validate}
        initialValues={{
            invoice_po_number: data.invoice_po_number,
            accounts_receivable_email: data.accounts_receivable_email,
            monthly_usage_fee: data.monthly_usage_fee,
            convenience_fee: data.convenience_fee,
            fixed_fee: data.fixed_fee,
            deposit_fee: data.deposit_fee,
            bill_parent_program: data.bill_parent_program,
            percent_total_spend_rebate: data.percent_total_spend_rebate,
            expiration_rebate_percentage: data.expiration_rebate_percentage,
            discount_rebate_percentage: data.discount_rebate_percentage,
            administrative_fee_calculation: data.program_extras? data.program_extras.administrative_fee_calculation: 'participants',
            administrative_fee: data.program_extras ? data.program_extras.administrative_fee : '0',
            administrative_fee_factor: data.program_extras ? data.program_extras.administrative_fee_factor: 0,
            allow_creditcard_deposits: data.program_extras ? data.program_extras.allow_creditcard_deposits: 0,
            reserve_percentage: data.reserve_percentage,
            transaction_fee: data.transaction_fee,
            create_invoices: data.create_invoices,
            use_parent_program_funds: data.use_parent_program_funds,
            allow_partial_funding: data.allow_partial_funding,
            is_pay_in_advance: data.is_pay_in_advance,
            country: data.country,
            send_balance_threshold_notification: data.send_balance_threshold_notification,
            balance_threshold: data.balance_threshold,
            low_balance_email: data.low_balance_email,
            cc_email_list: data.cc_email_list,
            bcc_email_list: data.bcc_email_list,
            prefix: data.prefix,
            notification_email_list: data.notification_email_list,
            root_fixed_fee : data.root_fixed_fee
        }}
        >
        {({ handleSubmit, form, submitting, pristine, values }) => (
        <form className="form" onSubmit={handleSubmit}>
            <ModalHeader className='w100'>
                <Row className='w100'>
                    <Col md="6" lg="6" xl="6">
                        <h3>Accounting</h3>
                        <h5 className="colorgrey">{data.name}</h5>
                    </Col>
                    <Col md="6" lg="6" xl="6" className='text-right'>
                        <ButtonToolbar className="modal__footer flex justify-content-right w100">
                            <Button outline color="primary" className="mr-3" onClick={toggle}>Close</Button>{' '}
                            <Button type="submit" disabled={loading} className="btn btn-primary" color="#ffffff">Save</Button>
                        </ButtonToolbar>
                    </Col>
                </Row>
            </ModalHeader>
            <ModalBody className='modal-lg'>
                <Row>
                    <Col md="6" lg="4" xl="4">
                        <Field name="country">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Country</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Country" />
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                </Row>
                <Row className='w100'>
                    <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField
                                name="is_pay_in_advance"
                                label="Pay in Advance"
                                checked={programCategories.is_pay_in_advance}
                                onChange={() => {
                                    const newValue = !programCategories.is_pay_in_advance;
                                    setProgramCategories((prevState) => (
                                        {is_pay_in_advance: newValue,
                                        invoice_for_awards: prevState.is_pay_in_advance}));
                                    handleCascadeFieldChange('is_pay_in_advance', newValue);
                                }}
                            />
                        </div>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField
                                name="invoice_for_awards"
                                label="Invoice for Awards"
                                checked={programCategories.invoice_for_awards}
                                onChange={() => {
                                    const newValue = !programCategories.invoice_for_awards;
                                    setProgramCategories((prevState) => (
                                        {is_pay_in_advance: prevState.invoice_for_awards,
                                        invoice_for_awards: newValue}));
                                    handleCascadeFieldChange('invoice_for_awards', newValue);
                                }}
                            />
                        </div>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField
                                name="create_invoices"
                                label="Create invoice"
                                checked={data.create_invoices}
                                onChange={() => {
                                    data.create_invoices = !data.create_invoices;
                                    handleCascadeFieldChange('create_invoices', data.create_invoices);
                                }}
                            />
                        </div>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField 
                                name="allow_creditcard_deposits"
                                label="Allow credit card deposit"
                                checked={data.program_extras ? data.program_extras.allow_creditcard_deposits : 0}
                                onChange={() => {
                                    data.program_extras.allow_creditcard_deposits = !data.program_extras.allow_creditcard_deposits
                                }}
                            />
                        </div>
                    </Col>
                    {
                        data?.parent_id === null && <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField 
                                name="root_fixed_fee"
                                label="Set fixed fee for root program only"
                                checked={data.root_fixed_fee}
                                onChange={() => {data.root_fixed_fee = !data.root_fixed_fee}}
                            />
                        </div>
                    </Col>
                    }

                    <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField
                                name="use_parent_program_funds"
                                label="Use parent program funds"
                                checked={data.use_parent_program_funds || 0}
                                onChange={() => {
                                    data.use_parent_program_funds = !data.use_parent_program_funds
                                }}
                            />
                        </div>
                    </Col>

                    <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField
                                name="allow_partial_funding"
                                label="Allow Partial Funding"
                                checked={data.allow_partial_funding || 0}
                                onChange={() => {
                                    data.allow_partial_funding = !data.allow_partial_funding;
                                    handleCascadeFieldChange('allow_partial_funding', data.allow_partial_funding);
                                }}
                            />
                        </div>
                    </Col>



                </Row>
                <Row>
                    <Col md="6" lg="4" xl="4">
                        <Field name="transaction_fee">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Transaction fee</span>
                                    {programTransactionFee.map((item, index) => (
                                        <Row key={index}>
                                            <Col md="6" lg="4" xl="4">
                                               <input
                                                   style={{width:'90px'}}
                                                    placeholder="Tier Amount"
                                                    title="Tier Amount"
                                                    value={item.tier_amount}
                                                    onChange={(e) => handleChangeTierAmount(index, e.target.value)}
                                                />
                                            </Col>
                                            <Col md="6" lg="4" xl="6">
                                                <input
                                                    style={{width:'120px'}}
                                                    placeholder="Transaction Fee"
                                                    title="Transaction Fee"
                                                    value={item.transaction_fee}
                                                    onChange={(e) => handleChangeTransactionFee(index, e.target.value)}
                                                />
                                            </Col>
                                            <Col md="4" lg="6" xl="1">
                                                <span style={{cursor: "pointer",color:'#4ce1b6'}} onClick={() => handleRemoveTier(index)}>Remove</span>
                                            </Col>
                                        </Row>
                                    ))}
                                    <Row>
                                        <Col><span style={{cursor: "pointer",color:'#4ce1b6'}}
                                                   onClick={handleAddTier}>Add Tier Amount</span></Col>
                                    </Row>

                            </div>
                        )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="reserve_percentage">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Reserved percentage</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Reserved percentage" />
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('reserve_percentage', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="discount_rebate_percentage">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Discount rebate percentage</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Discount rebate percentage" />
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('discount_rebate_percentage', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                </Row>
                <Row>
                    <Col md="6" lg="4" xl="4">
                        <Field name="expiration_rebate_percentage">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Expiration rebate percentage</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Expiration rebate percentage" />
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('expiration_rebate_percentage', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="percent_total_spend_rebate">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Rebate percentage of total spend</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Rebate percentage of total spend" />
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('percent_total_spend_rebate', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                </Row>
                <Row>
                    <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField
                                name="bill_parent_program"
                                label="Bill parent program"
                                checked={data.bill_parent_program ? data.bill_parent_program : 0}
                                onChange={() => {data.bill_parent_program = !data.bill_parent_program}}
                            />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md="6" lg="4" xl="4">
                        <Field name="administrative_fee_factor">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Custom Factor</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Custom Factor" />
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('administrative_fee_factor', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="Calculation">
                            {({ input, meta }) => (
                                <div className="form__form-group">
                                    <span className="form__form-group-label">Calculation</span>
                                    <Field
                                        name="administrative_fee_calculation"
                                        component={renderSelectField}
                                        options={[
                                            {label: "Participants", value: "participants"},
                                            {label: "Units", value: "units"},
                                            {label: "Custom", value: "custom"}
                                        ]}
                                        parse={(value) => { handleCascadeFieldChange('administrative_fee_calculation', value?.value ?? value); return value; }}
                                    />
                                </div>
                            )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="administrative_fee">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Fee amount</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input}
                                               placeholder="Fee amount"
                                        />
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('administrative_fee', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                </Row>
                <Row>
                    <Col md="6" lg="4" xl="4">
                        <Field name="deposit_fee">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Deposit fee</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <span style={{ whiteSpace: 'nowrap' }}><input type="text" {...input} placeholder="Deposit fee" />%</span>
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('deposit_fee', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="fixed_fee">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Fixed fee</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Fixed fee" />
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('fixed_fee', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="convenience_fee">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Convenience fee</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <span style={{ whiteSpace: 'nowrap' }}><input type="text" {...input} placeholder="Convenience fee" />%</span>
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('convenience_fee', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                </Row>
                <Row>
                    <Col md="6" lg="4" xl="4">
                        <Field name="monthly_usage_fee">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Monthly usage fee</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <span style={{ whiteSpace: 'nowrap' }}><input type="text" {...input} placeholder="Monthly usage fee" />%</span>
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('monthly_usage_fee', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="accounts_receivable_email">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Accounts receivable email</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Accounts receivable email" />
                                        <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('accounts_receivable_email', input.value)}>Save</Button>
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="invoice_po_number">
                        {({ input, meta }) => (
                            <div className="form__form-group">
                                <span className="form__form-group-label">Invoice PO number</span>
                                <div className="form__form-group-field">
                                    <div className="form__form-group-row">
                                        <input type="text" {...input} placeholder="Invoice PO number" />
                                        {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                    </div>
                                </div>
                            </div>
                        )}
                        </Field>
                    </Col>
                </Row>
                <hr />
              <h5 className="thick size16 mb-4">Email Settings</h5>
              <Row>
                <Col md="6" lg="4" xl="4">
                  <Field name="prefix">
                    {({ input, meta }) => (
                      <div className="form__form-group">
                        <span className="form__form-group-label">Prefix</span>
                        <div className="form__form-group-field">
                          <div className="form__form-group-row">
                            <input
                              type="text"
                              {...input}
                              placeholder="Prefix"
                            />
                            {meta.touched && meta.error && (
                              <span className="form__form-group-error">
                                {meta.error}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Field>
                </Col>
                <Col md="6" lg="4" xl="4">
                  <Field name="bcc_email_list">
                    {({ input, meta }) => (
                      <div className="form__form-group">
                        <span className="form__form-group-label">
                          BCC Email List
                        </span>
                        <div className="form__form-group-field">
                          <div className="form__form-group-row">
                            <input
                              type="text"
                              {...input}
                              placeholder="BCC Email List"
                            />
                            <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('bcc_email_list', input.value)}>Save</Button>
                            {meta.touched && meta.error && (
                              <span className="form__form-group-error">
                                {meta.error}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Field>
                </Col>
                <Col md="6" lg="4" xl="4">
                  <Field name="cc_email_list">
                    {({ input, meta }) => (
                      <div className="form__form-group">
                        <span className="form__form-group-label">
                          CC Email List
                        </span>
                        <div className="form__form-group-field">
                          <div className="form__form-group-row">
                            <input
                              type="text"
                              {...input}
                              placeholder="CC Email List"
                            />
                            <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('cc_email_list', input.value)}>Save</Button>
                            {meta.touched && meta.error && (
                              <span className="form__form-group-error">
                                {meta.error}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Field>
                </Col>
              </Row>
              <Row>
                <Col md="6" lg="4" xl="4">
                  <Field name="notification_email_list">
                    {({ input, meta }) => (
                      <div className="form__form-group">
                        <span className="form__form-group-label">
                          Deposit Notification Email
                        </span>
                        <div className="form__form-group-field">
                          <div className="form__form-group-row">
                            <input
                              type="text"
                              {...input}
                              placeholder="Deposit Notification Email"
                            />
                            <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('notification_email_list', input.value)}>Save</Button>
                            {meta.touched && meta.error && (
                              <span className="form__form-group-error">
                                {meta.error}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Field>
                </Col>
              </Row>
                <Row>
                    <Col md="12">
                        <hr/>
                        <h4 style={{marginBottom: '10px'}}>Balance Notfication</h4>
                    </Col>
                </Row>
                <Row>
                    <Col md="6" lg="4" xl="4">
                        <div className="form__form-group">
                            <CheckboxField
                              name="send_balance_threshold_notification"
                              label="Send the Low Deposit Balance Notification"
                              checked={data.send_balance_threshold_notification}
                              onChange={() => {
                                  data.send_balance_threshold_notification = !data.send_balance_threshold_notification;
                                  handleCascadeFieldChange('send_balance_threshold_notification', data.send_balance_threshold_notification);
                              }}
                            />
                        </div>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="balance_threshold">
                            {({ input, meta }) => (
                              <div className="form__form-group">
                                  <span className="form__form-group-label">Balance Threshold</span>
                                  <div className="form__form-group-field">
                                      <div className="form__form-group-row">
                                          <input type="text" {...input} placeholder="" />
                                          <Button type="button" color="primary" size="sm" className="ml-2" onClick={() => handleCascadeFieldChange('balance_threshold', input.value)}>Save</Button>
                                          {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                      </div>
                                  </div>
                              </div>
                            )}
                        </Field>
                    </Col>
                    <Col md="6" lg="4" xl="4">
                        <Field name="low_balance_email">
                            {({ input, meta }) => (
                              <div className="form__form-group">
                                  <span className="form__form-group-label">Low Deposit Balance Email</span>
                                  <div className="form__form-group-field">
                                      <div className="form__form-group-row">
                                          <input type="text" {...input} placeholder="" />
                                          {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                      </div>
                                  </div>
                              </div>
                            )}
                        </Field>
                    </Col>
                </Row>
            </ModalBody>
        </form>
        )}
        </Form>
    </Modal>
    <HierarchySaveModal
      isOpen={isCascadeConfirmOpen}
      toggle={() => setCascadeConfirmOpen(false)}
      organizationId={data.organization_id}
      programId={data.id}
      settingsGroup="accounting"
      fields={activeCascadeField ? { [activeCascadeField.name]: activeCascadeField.value } : {}}
      dispatch={dispatch}
      onSuccess={() => {
        dispatch(getProgramAction(data.organization_id, data.id));
        setActiveCascadeField(null);
      }}
    />
    </>
    )
}
AccountingModal.propTypes = {
  theme: ThemeProps.isRequired,
  rtl: RTLProps.isRequired,
  organization: Object.isRequired,
  data: Object.isRequired
};

export default withRouter(connect((state) => ({
  theme: state.theme,
  rtl: state.rtl,
  organization: state.organization,
  data: state.program,
  auth: state.auth
}))(AccountingModal));
